//@flow
import React from 'react';
import ProductsCollection from '../../Stores/Products/productsCollection';
import CheckoutButton from './CheckoutButton.react';
import UpsellSnackCard from '../../Upsell/components/UpsellSnackCard.react';
import { Link } from 'react-router';
import UpsellUtils from '../../Utils/Upsell';
import notification from '../../Notification/components/StandardNotification.react';
import Constants from '../../constants';
import GiftUtils from '../../Utils/gifts';

import type QuoteModel from '../../Stores/Quote/quoteModel';
import type QuoteItemsCollection from '../../Stores/Quote/quoteItemsCollection';
import type CustomerModel from '../../Stores/Customer/customerModel';
import type { NotificationProps } from '../../Notification/components/StandardNotification.react';
import type ProductModel from '../../Stores/Products/productModel';
import type CategoriesCollection from '../../Stores/MemberCategories/categoriesCollection';
import type StockCollection from '../../Stores/Stock/stockCollection';
import type MembershipPlanModel from '../../Stores/Membership/membershipPlanModel';

type ExternalProps = {
  products:ProductsCollection,
  quote:QuoteModel,
  quoteItems:QuoteItemsCollection,
  quoteId:?string,
  newMember?:boolean,
  children?:any,
  customer:CustomerModel,
  activeSubQuoteId:?string,
  categories:CategoriesCollection,
  stock:StockCollection,
  membershipPlan:MembershipPlanModel,
};

type Props = ExternalProps&NotificationProps&{
  data:{
    sku:string,
    qty:number,
    quoteId:string,
  },
};

function _isGiftProduct(props:Props):boolean {
  if (!props.data) return false;
  const hasEgift = props.data.sku === GiftUtils.EGIFT_SKU;
  const hasGift = props.categories.hasSkuForUrlKey('m-gifts', props.data.sku);
  return hasGift || hasEgift;
}

function _getCheckoutButton(props:Props) {
  if (_isGiftProduct(props)) {
    return (
      <CheckoutButton
        quoteItems={props.quoteItems}
        newMember={!props.newMember}
        customer={props.customer}
        id="CartNotification-checkout-button"
      >
        Checkout
      </CheckoutButton>
    );
  }
}

function _isSubscription(props) {
  return props.activeSubQuoteId && props.data && props.data.quoteId === props.activeSubQuoteId;
}

function _getSubscriptionLink(props:Props) {
  if (!_isSubscription(props)) return null;

  return (
    <div className="view-order">
      <Link to="/account/subscription">View your entire subscription order</Link>
    </div>
  );
}

function _getCartMessage(props:Props) {
  if (_isUpgradeSubscription(props)) return 'Subscription updated';

  const quantity = props.data && props.data.qty;
  const quoteType = _isSubscription(props) ? 'subscription' : 'cart';

  return (!quantity) ? `Removed from ${quoteType}` : `${quantity} in ${quoteType}`;
}

function shouldRender(props:Props):boolean {
  if (!_getProduct(props)) return false;

  // don't show notification if it doesn't belong to the active quote or active subscription quote.
  if (
    props.data.quoteId !== props.quoteId &&
    props.data.quoteId !== props.activeSubQuoteId
  ) return false;
  return true;
}

function timeout(props:Props):number {
  return _isUpgradeSubscription(props) || _getUpsellData(props) ? 8000 : 3000;
}

function _getProduct(props:Props):?ProductModel {
  return props.products.getProductBySku(props.data.sku);
}

function _isUpgradeSubscription(props:Props) {
  return props.data.sku === '6014DR12';
}

function _getUpsellData(props:Props) {
  const upsellProductData = UpsellUtils.getUpsellProductDataFromSku(
    props.data.sku,
    props.quoteItems.getSkus(),
    props.stock,
    props.products,
  );
  if (!upsellProductData) return;
  const upsellProduct = props.products.getProductBySku(upsellProductData.sku);
  if (!upsellProduct) return;
  const upsellData = { tagline: upsellProductData.tagline, product: upsellProduct };
  return props.data.qty !== 0 && upsellData;
}

function _getUpsellSnackCard(props:Props) {
  const upsellData = _getUpsellData(props);
  if (!upsellData) return;

  return (
    <UpsellSnackCard
      categories={props.categories}
      product={upsellData.product}
      quote={props.quote}
      quoteItems={props.quoteItems}
      membershipPlan={props.membershipPlan}
    >
      {upsellData.tagline}
    </UpsellSnackCard>
  );
}

function CartNotification(props:Props) {
  const product = _getProduct(props);
  if (!product) return null;

  return (
    <div className="cart-notif-content">
      <div className="cart-notif-item">
        <img src={product.getSmallSquareImageUrl(64, 64)} alt=""/>
        <div className="cart-notif-item-details">
          <p className="cart-notif-count">{_getCartMessage(props)}</p>
          <p className="cart-notif-item-name">{product.getName()}</p>
        </div>
      </div>
      { _getCheckoutButton(props) }
      { _getSubscriptionLink(props) }
      { _getUpsellSnackCard(props) }
    </div>
  );
}

export default notification<ExternalProps>(CartNotification, {
  name: Constants.CART_NOTIFICATION,
  className: 'cart-notification',
  timeout,
  shouldRender,
});
